import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

// ── Types ────────────────────────────────────────────────────────────────────

interface LogEntry {
  id: number;
  integration: string;
  severity: "Info" | "Warning" | "Error";
  category: string;
  message: string;
  additionalInfo?: string;
  suppressedCount: number;
  createdAt: number; // Unix ms
}

interface LogPage {
  total: number;
  page: number;
  pageSize: number;
  items: LogEntry[];
}

interface Props {
  /** If provided, scopes to this integration slug via GET /Integration/Logs/{slug} */
  integrationSlug?: string;
}

// ── Badge helpers ─────────────────────────────────────────────────────────────

const SEVERITY_STYLES: Record<string, string> = {
  Info:    "bg-slate-100 text-slate-600",
  Warning: "bg-amber-100 text-amber-700",
  Error:   "bg-red-100 text-red-700",
};

const CATEGORY_STYLES: Record<string, string> = {
  Auth:     "bg-purple-50 text-purple-600",
  Security: "bg-orange-50 text-orange-700",
  Config:   "bg-cornflower-blue-50 text-cornflower-blue-700",
  Request:  "bg-slate-50 text-slate-500",
  Response: "bg-slate-50 text-slate-500",
  AiTool:   "bg-green-50 text-green-700",
  Setup:    "bg-teal-50 text-teal-700",
  Error:    "bg-red-50 text-red-500",
};

function SeverityBadge({ severity }: { severity: string }) {
  const cls = SEVERITY_STYLES[severity] ?? "bg-slate-100 text-slate-500";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {severity}
    </span>
  );
}

function CategoryBadge({ category }: { category: string }) {
  const cls = CATEGORY_STYLES[category] ?? "bg-slate-50 text-slate-500";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>
      {category}
    </span>
  );
}

function formatTs(ms: number) {
  return new Date(ms).toLocaleString("en-US", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).replace(",", "");
}

// ── Main component ─────────────────────────────────────────────────────────────

const SEVERITY_OPTIONS = ["All", "Info", "Warning", "Error"];
const CATEGORY_OPTIONS = ["All", "Auth", "Config", "Request", "Response", "Security", "AiTool", "Setup", "Error"];
const PAGE_SIZES = [25, 50, 100];

export default function IntegrationActivityLog({ integrationSlug }: Props) {
  const api = useApi();

  const [data, setData] = useState<LogPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [severity,    setSeverity]    = useState("All");
  const [category,    setCategory]    = useState("All");
  const [integration, setIntegration] = useState("All");
  const [search,      setSearch]      = useState("");
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(50);

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // All integration slugs — used in the cross-integration filter dropdown.
  // MANDATORY: add your slug here when building a new integration so it appears
  // in both the global log view and the per-integration activity log tab.
  // Slug must match the string passed to IntegrationLogger.LogAsync() in C#.
  const INTEGRATIONS = [
    "All",
    // CRM
    "NetSuite", "HubSpot", "Salesforce", "Dynamics", "Pipedrive", "Zoho", "NativeCrm",
    // E-commerce
    "Shopify", "BigCommerce", "WooCommerce", "Magento", "Klaviyo",
    // Government / Vertical
    "Accela", "Formstack",
    // Hospitality
    "Uplisting", "Guesty", "Cloudbeds",
    // POS / Booking / Payments
    "Square", "Toast", "Mindbody", "QuickBooks", "Stripe",
    // Scheduling
    "OutlookCalendar", "Calendly",
    // Notifications
    "TeamsNotifications", "SlackNotifications",
    // Channels
    "WhatsApp",
    // Automation
    "Zapier", "PowerAutomate", "Make",
    // Platform
    "MCP", "Microsoft365", "TranscriptStorage", "GoogleTagManager",
    // ITSM
    "ServiceNow",
    // Recruiting
    "Recruiting",
  ];

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (severity    !== "All") params.set("severity",    severity);
      if (category    !== "All") params.set("category",    category);
      if (!integrationSlug && integration !== "All") params.set("integration", integration);

      const url = integrationSlug
        ? `Integration/Logs/${integrationSlug}?${params}`
        : `Integration/Logs?${params}`;

      const resp = await api.messaging.get(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json: LogPage = await resp.json();
      setData(json);
    } catch (e) {
      setError("Failed to load integration logs.");
    } finally {
      setLoading(false);
    }
  }, [api.messaging, integrationSlug, severity, category, integration, page, pageSize]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [severity, category, integration, pageSize]);

  function toggleExpand(id: number) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // Client-side search filter on visible rows
  const visibleItems = (data?.items ?? []).filter(item => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      item.message.toLowerCase().includes(q) ||
      item.category.toLowerCase().includes(q) ||
      item.integration.toLowerCase().includes(q) ||
      (item.additionalInfo ?? "").toLowerCase().includes(q)
    );
  });

  const totalPages = data ? Math.ceil(data.total / pageSize) : 1;

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search logs…"
          className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-cornflower-blue-500/30 focus:border-cornflower-blue-500 w-52"
          aria-label="Search log messages"
        />

        <select
          value={severity}
          onChange={e => setSeverity(e.target.value)}
          className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-cornflower-blue-500/30 focus:border-cornflower-blue-500"
          aria-label="Filter by severity"
        >
          {SEVERITY_OPTIONS.map(s => <option key={s} value={s}>{s === "All" ? "All severities" : s}</option>)}
        </select>

        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-cornflower-blue-500/30 focus:border-cornflower-blue-500"
          aria-label="Filter by category"
        >
          {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c === "All" ? "All categories" : c}</option>)}
        </select>

        {!integrationSlug && (
          <select
            value={integration}
            onChange={e => setIntegration(e.target.value)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-cornflower-blue-500/30 focus:border-cornflower-blue-500"
            aria-label="Filter by integration"
          >
            {INTEGRATIONS.map(i => <option key={i} value={i}>{i === "All" ? "All integrations" : i}</option>)}
          </select>
        )}

        {(severity !== "All" || category !== "All" || integration !== "All" || search) && (
          <button
            type="button"
            onClick={() => { setSeverity("All"); setCategory("All"); setIntegration("All"); setSearch(""); }}
            className="px-2 py-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-colors"
          >
            Clear filters
          </button>
        )}

        <span className="ml-auto text-xs text-slate-400">
          {data ? `${data.total} total entries` : ""}
        </span>

        <button
          type="button"
          onClick={fetchLogs}
          className="px-2 py-1.5 text-xs text-cornflower-blue-600 hover:text-cornflower-blue-800 border border-cornflower-blue-200 rounded-lg transition-colors"
          aria-label="Refresh logs"
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        {loading && (
          <div className="text-center py-6 text-xs text-slate-400">Loading…</div>
        )}
        {error && (
          <div className="text-center py-6 text-xs text-red-500">{error}</div>
        )}
        {!loading && !error && visibleItems.length === 0 && (
          <div className="text-center py-10 text-xs text-slate-400">
            No log entries found.{" "}
            {(severity !== "All" || category !== "All" || search) && "Try clearing the filters."}
          </div>
        )}
        {!loading && visibleItems.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 text-left text-slate-500 font-medium w-36">Timestamp</th>
                {!integrationSlug && (
                  <th className="px-3 py-2 text-left text-slate-500 font-medium w-24">Integration</th>
                )}
                <th className="px-3 py-2 text-left text-slate-500 font-medium w-20">Severity</th>
                <th className="px-3 py-2 text-left text-slate-500 font-medium w-24">Category</th>
                <th className="px-3 py-2 text-left text-slate-500 font-medium">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleItems.map(item => {
                const expanded = expandedIds.has(item.id);
                const hasDetail = item.additionalInfo && item.additionalInfo.trim().length > 0;
                return (
                  <React.Fragment key={item.id}>
                    <tr
                      className={`${hasDetail ? "cursor-pointer hover:bg-slate-50" : ""} transition-colors`}
                      onClick={() => hasDetail && toggleExpand(item.id)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-slate-500 font-mono">
                        {formatTs(item.createdAt)}
                      </td>
                      {!integrationSlug && (
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600 font-medium">
                          {item.integration}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <SeverityBadge severity={item.severity} />
                      </td>
                      <td className="px-3 py-2">
                        <CategoryBadge category={item.category} />
                      </td>
                      <td className="px-3 py-2 text-slate-700 break-words">
                        <span>{item.message}</span>
                        {item.suppressedCount > 0 && (
                          <span className="ml-2 text-[10px] text-amber-600 font-medium">
                            +{item.suppressedCount} suppressed
                          </span>
                        )}
                        {hasDetail && (
                          <span className="ml-2 text-[10px] text-cornflower-blue-500">
                            {expanded ? "▲ hide details" : "▼ details"}
                          </span>
                        )}
                      </td>
                    </tr>
                    {expanded && hasDetail && (
                      <tr className="bg-slate-50">
                        <td colSpan={integrationSlug ? 4 : 5} className="px-3 py-2">
                          <pre className="text-[11px] text-slate-600 whitespace-pre-wrap break-words font-mono bg-slate-100 rounded p-2 max-h-40 overflow-y-auto">
                            {item.additionalInfo}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Page {page} of {totalPages} &mdash; {data.total} entries</span>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40 hover:bg-slate-100"
              aria-label="First page"
            >«</button>
            <button
              type="button"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40 hover:bg-slate-100"
              aria-label="Previous page"
            >‹</button>
            <span className="px-2">{page}</span>
            <button
              type="button"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40 hover:bg-slate-100"
              aria-label="Next page"
            >›</button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40 hover:bg-slate-100"
              aria-label="Last page"
            >»</button>
          </div>

          <div className="flex items-center gap-2">
            <span>Per page:</span>
            <select
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              className="text-xs border border-slate-200 rounded px-1 py-0.5 focus:outline-none"
              aria-label="Rows per page"
            >
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
